import { cn } from "@app/ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { isDemoBoot } from "../../lib/demo-mode.js";
import { useTodayLayoutStore } from "../../stores/todayLayout.store.js";
import {
  columnsForWidth,
  DEFAULT_TODAY_LAYOUT,
  moduleSpan,
  moveModule,
  nextSpan,
  packedRangeStack,
  packedRangesOverlap,
  packModules,
  type MoveAnchor,
  type PackedModule,
  type TodayModuleId,
} from "./layout.js";
import { TodayModuleShell } from "./TodayModuleShell.js";

interface Props {
  /** Module id → its rendered node; null/undefined modules are skipped (their
   *  slot simply doesn't render — e.g. no overdue section when nothing is due). */
  modules: Partial<Record<TodayModuleId, React.ReactNode>>;
}

/** Where an in-flight drag would land: target column, the neighbor anchors the
 *  drop would splice next to, and the insertion bar's y offset. */
interface DropPreview {
  col: number;
  anchor: MoveAnchor;
  top: number;
  height: number;
}

/** Gap between modules (px) — matches the rest of the screen's gap-5. */
const GAP = 20;
/** How far neighbors visually part around the insertion point (px, each way).
 *  Pure transform — the packed flow never changes, so the dragged module's
 *  transform anchor stays put (a real gap would jump it off the pointer). */
const PART_SHIFT = 5;

/**
 * The Today screen's modular canvas: modules flow into 1–3 columns depending
 * on container width (ResizeObserver). Placement is a measured-height SKYLINE
 * PACK (`packModules`): each module rests on the highest free spot of its
 * column range, so a widened module (hover width-toggle, `span`) pushes down
 * only its own columns while the others keep flowing beside it. Modules are
 * absolutely positioned from the pack and glide between placements through a
 * hand-rolled FLIP on motion values. Each module is relocatable through a
 * captured-pointer drag on its hover grip;
 * the drop resolves from the RELEASE point to a column + neighbor anchor,
 * committed to the device-local layout store. While a drag is in flight the
 * SAME resolver drives a live preview: column highlight, insertion bar, and
 * neighbors parting a few px (transform-only). All drag-time geometry comes
 * from PACK data, never client rects — rects include the parting/FLIP
 * transforms and would feed back into the resolution (bar jitter).
 */
export function TodayGrid({ modules }: Props) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [outerW, setOuterW] = useState(0);
  const [innerW, setInnerW] = useState(0);
  const order = useTodayLayoutStore((s) => s.order);
  const column = useTodayLayoutStore((s) => s.column);
  const span = useTodayLayoutStore((s) => s.span);
  const move = useTodayLayoutStore((s) => s.move);
  const setSpan = useTodayLayoutStore((s) => s.setSpan);
  const [heights, setHeights] = useState<Partial<Record<TodayModuleId, number>>>({});
  const [dragging, setDragging] = useState<TodayModuleId | null>(null);
  const [drop, setDrop] = useState<DropPreview | null>(null);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const apply = () => {
      setOuterW(outer.clientWidth);
      setInnerW(inner.clientWidth);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  const cols = columnsForWidth(outerW);
  const colWidth = innerW > 0 ? (innerW - GAP * (cols - 1)) / cols : 0;
  // Ghost demos share the host origin. Keep their layout deterministic and
  // non-interactive so the iframe can never write the user's local layout.
  const customizable = !isDemoBoot();
  const layout = customizable ? { order, column, span } : DEFAULT_TODAY_LAYOUT;
  const present = (id: TodayModuleId) => modules[id] != null;
  const shown = layout.order.filter(present);
  const shownKey = shown.join("|");

  // Conditional modules (next-up/overdue/snoozed/upcoming) can disappear and
  // later return with very different content. Forget their old measurements
  // while absent so a returning card stays hidden until its fresh height is
  // measured instead of packing visibly from a stale height for one frame.
  useLayoutEffect(() => {
    setHeights((prev) => {
      let next = prev;
      for (const id of Object.keys(prev) as TodayModuleId[]) {
        if (present(id)) continue;
        if (next === prev) next = { ...prev };
        delete next[id];
      }
      return next;
    });
    // `shownKey` is the stable presence signature; `modules` itself is rebuilt
    // on every minute tick even when no module appeared or disappeared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey]);

  const pack = packModules(layout, cols, (id) => heights[id], GAP, present);
  const placedOf = new Map<TodayModuleId, PackedModule>(pack.placed.map((p) => [p.id, p]));
  const heightOf = (id: TodayModuleId) => heights[id] ?? 0;
  const leftOf = (col: number) => col * (colWidth + GAP);

  /** Modules occupying any part of a target footprint, ordered visually. */
  function rangeStack(col: number, span: number, except: TodayModuleId): PackedModule[] {
    return packedRangeStack(pack.placed, col, span, heightOf, except);
  }

  /** The (column, anchor) a release at this point would commit, plus where the
   *  insertion bar renders. ONE resolver for the live preview and the drop
   *  itself, so the preview can never lie. */
  function targetAt(id: TodayModuleId, clientX: number, clientY: number): DropPreview | null {
    const inner = innerRef.current;
    if (!inner || colWidth <= 0) return null;
    const rect = inner.getBoundingClientRect();
    if (
      clientX < rect.left - 80 || clientX > rect.right + 80
      || clientY < rect.top - 80 || clientY > rect.bottom + 80
    ) return null;
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    const targetSpan = moduleSpan(layout, id, cols);
    const maxCol = Math.max(0, cols - targetSpan);
    const col = Math.max(0, Math.min(maxCol, Math.floor(relX / (colWidth + GAP))));
    const stack = rangeStack(col, targetSpan, id);
    let index = 0;
    for (const p of stack) if (relY > p.top + heightOf(p.id) / 2) index++;
    const before = stack[index];
    const after = stack[index - 1];
    const anchor: MoveAnchor = before ? { beforeId: before.id } : after ? { afterId: after.id } : {};
    // Preview the ACTUAL prospective skyline. In a multi-column footprint the
    // selected neighbor's top is insufficient: another covered column may be
    // taller and determine where the module can really rest.
    const prospective = packModules(
      moveModule(layout, id, col, anchor, cols), cols, (moduleId) => heights[moduleId], GAP, present,
    );
    const top = prospective.placed.find((item) => item.id === id)?.top;
    if (top == null) return null;
    return { col, anchor, top, height: prospective.height };
  }

  function handleDrop(id: TodayModuleId, clientX: number, clientY: number) {
    setDrop(null);
    const target = targetAt(id, clientX, clientY);
    if (target) move(id, target.col, target.anchor, cols);
  }

  function handleKeyboardMove(id: TodayModuleId, key: "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown") {
    const current = placedOf.get(id);
    if (!current) return;
    if (key === "ArrowLeft" || key === "ArrowRight") {
      const maxCol = Math.max(0, cols - current.span);
      const col = Math.max(0, Math.min(maxCol, current.col + (key === "ArrowLeft" ? -1 : 1)));
      if (col === current.col) return;
      const stack = rangeStack(col, current.span, id);
      const middle = current.top + heightOf(id) / 2;
      const index = stack.findIndex((item) => middle <= item.top + heightOf(item.id) / 2);
      const before = index >= 0 ? stack[index] : undefined;
      const after = index > 0 ? stack[index - 1] : index < 0 ? stack[stack.length - 1] : undefined;
      move(id, col, before ? { beforeId: before.id } : after ? { afterId: after.id } : {}, cols);
      return;
    }

    const stack = packedRangeStack(pack.placed, current.col, current.span, heightOf);
    const index = stack.findIndex((item) => item.id === id);
    const neighbor = key === "ArrowUp" ? stack[index - 1] : stack[index + 1];
    if (!neighbor) return;
    move(
      id,
      current.col,
      key === "ArrowUp" ? { beforeId: neighbor.id } : { afterId: neighbor.id },
      cols,
    );
  }

  function updateDrop(id: TodayModuleId, clientX: number, clientY: number) {
    const target = targetAt(id, clientX, clientY);
    setDrop((prev) =>
      prev?.col === target?.col
        && prev?.top === target?.top
        && prev?.height === target?.height
        && prev?.anchor.beforeId === target?.anchor.beforeId
        && prev?.anchor.afterId === target?.anchor.afterId
        ? prev
        : target,
    );
  }

  /** Parting shift for a placed module while a drag previews an insertion. */
  function partShift(id: TodayModuleId): number {
    if (!drop || !dragging || id === dragging) return 0;
    const p = placedOf.get(id);
    const targetSpan = moduleSpan(layout, dragging, cols);
    if (!p || !packedRangesOverlap(p.col, p.span, drop.col, targetSpan)) return 0;
    const stack = rangeStack(drop.col, targetSpan, dragging);
    const pos = stack.findIndex((s) => s.id === id);
    if (pos < 0) return 0;
    const insertion = drop.anchor.beforeId
      ? stack.findIndex((s) => s.id === drop.anchor.beforeId)
      : drop.anchor.afterId
        ? stack.findIndex((s) => s.id === drop.anchor.afterId) + 1
        : stack.length;
    if (insertion < 0) return 0;
    return pos < insertion ? -PART_SHIFT : PART_SHIFT;
  }

  const maxW = cols === 1 ? "max-w-3xl" : cols === 2 ? "max-w-[104rem]" : "max-w-[160rem]";
  const measuredAll = colWidth > 0;
  const dragSpan = dragging ? moduleSpan(layout, dragging, cols) : 1;
  const dragWidth = colWidth * dragSpan + GAP * (dragSpan - 1);

  return (
    <div ref={outerRef} className="w-full pt-3">
      <div ref={innerRef} className={cn("relative mx-auto", maxW)} style={{ height: Math.max(pack.height, 40) }}>
        {dragging && drop && (
          <div
            aria-hidden
            className="pointer-events-none absolute z-0 rounded-2xl ring-2 ring-primary/30"
            style={{
              left: leftOf(drop.col) - 6,
              top: -6,
              width: dragWidth + 12,
              height: Math.max(pack.height, drop.height) + 12,
            }}
          />
        )}
        {dragging && drop && (
          <div
            aria-hidden
            className="pointer-events-none absolute z-20 h-0.5 rounded-full bg-primary/70 shadow-sm transition-[top] duration-100 ease-out motion-reduce:transition-none"
            style={{ left: leftOf(drop.col) + 16, width: Math.max(0, dragWidth - 32), top: drop.top }}
          />
        )}
        {measuredAll && shown.map((id) => {
          const p = placedOf.get(id);
          const s = moduleSpan(layout, id, cols);
          const col = p?.col ?? Math.min(Math.max(0, Math.min(column[id] ?? 0, cols - 1)), cols - s);
          return (
            <TodayModuleShell
              key={id}
              id={id}
              left={leftOf(col)}
              // Parting rides the slot itself (absolute top, FLIP-animated) —
              // the dragged module always gets shift 0 from partShift.
              top={(p?.top ?? pack.height) + partShift(id)}
              width={colWidth * s + GAP * (s - 1)}
              measured={!!p}
              dragging={dragging === id}
              spanState={{ span: s, cols }}
              customizable={customizable}
              onHeight={(h) => setHeights((prev) => (prev[id] === h ? prev : { ...prev, [id]: h }))}
              onCycleSpan={() => setSpan(id, nextSpan(layout, id, cols))}
              onKeyboardMove={(key) => handleKeyboardMove(id, key)}
              onDragStart={() => setDragging(id)}
              onDragMove={(x, y) => updateDrop(id, x, y)}
              onDrop={(x, y) => {
                setDragging(null);
                handleDrop(id, x, y);
              }}
              onCancel={() => {
                setDragging(null);
                setDrop(null);
              }}
            >
              {modules[id]}
            </TodayModuleShell>
          );
        })}
      </div>
    </div>
  );
}
